// @flow
import {of, concat} from 'rxjs';
import {ofType, StateObservable} from 'redux-observable';
import {catchError, concatMap, map, switchMap, toArray} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {
  endpoints,
  getStoreLegalSettingApi,
  saveStoreLegalSettingApi,
} from './api';
import {buildRoute} from '../../../http';
import {uploadImageStoreApi} from '../../file-storage/api';
import {createSingleFieldFormData} from '../../../utils/form';
import {
  getStoreTermsConfigApi,
  saveStoreTermsConfigApi,
  getStoreShippingsConfigApi,
  saveStoreShippingsConfigApi,
  getStoreAboutUsConfigApi,
  saveStoreAboutUsConfigApi,
  saveStoreFaqConfigApi,
  getStoreFaqConfigApi,
  deleteStoreFaqConfigApi,
  editStoreFaqConfigApi,
  getStoreRefundsConfigApi,
  saveStoreRefundsConfigApi,
} from './api';

import type {RootState} from '../../index';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../../store/utils';
import type {StoredFile} from '../../../entities';
import type {ApiError} from '../../../http/error';
import type {SaveLegalRequest} from './api';

type UpdateLegalSetting = {
  aboutInformationEnabled?: boolean,
  complaintsSetting?: {
    enabled: boolean,
    formUrl?: string,
  },
  faqSettingEnabled?: boolean,
  refundInformationEnabled?: boolean,
  shippingInformationEnabled?: boolean,
  termsOfServiceInformationEnabled?: boolean,
};
export type LegalConfigAction = Action & {
  error?: string,
  legalSetting?: UpdateLegalSetting,
  legalRequest?: SaveLegalRequest,
};

export type FaqQuestion = {
  id?: string,
  question: string,
  answer: string,
  type?: string,
};

export type PoliciesAndPrivacy = {
  enabled: boolean,
  id: string,
  description: string,
  type: string,
};

export type Terms = {
  enabled: boolean,
  id: string,
  description: string,
  type: string,
};

export type Refunds = {
  enabled: boolean,
  id: string,
  description: string,
  type: string,
};

export type Shippings = {
  enabled: boolean,
  id: string,
  description: string,
};

export type AboutUs = {
  enabled: boolean,
  description1?: string,
  description2?: string,
  description3?: string,
  image1Id?: string,
  image2Id?: string,
  image3Id?: string,
  image4Id?: string,
  image5Id?: string,
  image6Id?: string,
  image4Label?: string,
  image5Label?: string,
  image6Label?: string,
  title?: string,
  type: string,
  storeAboutInformationType: 'SMALL' | 'MEDIUM' | 'LARGE',
};

export type LegalSetting = {
  aboutInformationEnabled?: boolean,
  faqSettingEnabled?: boolean,
  refundInformationEnabled?: boolean,
  shippingInformationEnabled?: boolean,
  policiesAndPrivacyEnabled?: boolean,
  termsOfServiceInformationEnabled?: boolean,
  complaintsSetting?: {
    enabled: boolean,
    formUrl?: string,
  },
};

export type LegalConfigState = {
  legalSetting?: LegalSetting,
  isSavingLegalSetting?: boolean,
  saveLegalSettingSuccess?: boolean,
  saveLegalSettingError?: string,
  isFetchingLegalSetting?: boolean,
  fetchLegalSettingSuccess?: boolean,
  fetchLegalSettingError?: boolean,
  isSavingLegalSetting?: boolean,
  saveLegalSettingError?: boolean,
  isSaving?: boolean,
  error?: string,
  terms?: Terms,
  questions?: FaqQuestion[],
  isFetchingTerms?: boolean,
  isFetchingFaq?: boolean,
  fetchTermsError?: string,
  saveTermsError?: string,
  saveFaqError?: string,
  fetchFaqError?: string,
  saveTermsSuccess?: boolean,
  saveFaqSuccess?: boolean,
  refunds?: Refunds,
  isFetchingRefunds?: boolean,
  fetchRefundsError?: string,
  saveRefundsError?: string,
  saveRefundsSuccess?: boolean,
  shippings?: Shippings,
  isFetchingShippings?: boolean,
  fetchShippingsError?: string,
  saveShippingsSuccess?: boolean,
  saveShippingsError?: string,
  isFetchingAboutUs?: boolean,
  fetchAboutUsError?: string,
  saveAboutUsError?: string,
  saveAboutUsSuccess?: boolean,
  aboutUs?: AboutUs,
};

export class FetchStoreLegalSetting extends ActionCreator<LegalConfigAction> {}
export class FetchStoreLegalSettingSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreLegalSettingError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreLegalSetting extends ActionCreator<LegalConfigAction> {}
export class SaveStoreLegalSettingSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreLegalSettingError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreTermsConfig extends ActionCreator<LegalConfigAction> {}
export class SaveStoreTermsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreTermsConfigError extends ActionCreator<LegalConfigAction> {}

export class FetchStoreTermsConfig extends ActionCreator<LegalConfigAction> {}
export class FetchStoreTermsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreTermsConfigError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreRefundsConfig extends ActionCreator<LegalConfigAction> {}
export class SaveStoreRefundsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreRefundsConfigError extends ActionCreator<LegalConfigAction> {}

export class FetchStoreRefundsConfig extends ActionCreator<LegalConfigAction> {}
export class FetchStoreRefundsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreRefundsConfigError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreShippingsConfig extends ActionCreator<LegalConfigAction> {}
export class SaveStoreShippingsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreShippingsConfigError extends ActionCreator<LegalConfigAction> {}

export class FetchStoreShippingsConfig extends ActionCreator<LegalConfigAction> {}
export class FetchStoreShippingsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreShippingsConfigError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreAboutUsConfig extends ActionCreator<LegalConfigAction> {}
export class SaveStoreAboutUsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreAboutUsConfigError extends ActionCreator<LegalConfigAction> {}

export class FetchStoreAboutUsConfig extends ActionCreator<LegalConfigAction> {}
export class FetchStoreAboutUsConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreAboutUsConfigError extends ActionCreator<LegalConfigAction> {}

export class FetchStoreFaqConfig extends ActionCreator<LegalConfigAction> {}
export class FetchStoreFaqConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class FetchStoreFaqConfigError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreFaqSetting extends ActionCreator<LegalConfigAction> {}
export class SaveStoreFaqSettingSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreFaqSettingError extends ActionCreator<LegalConfigAction> {}

export class SaveStoreFaqConfig extends ActionCreator<LegalConfigAction> {}
export class SaveStoreFaqConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class SaveStoreFaqConfigError extends ActionCreator<LegalConfigAction> {}

export class EditStoreFaqConfig extends ActionCreator<LegalConfigAction> {}
export class EditStoreFaqConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class EditStoreFaqConfigError extends ActionCreator<LegalConfigAction> {}

export class DeleteStoreFaqConfig extends ActionCreator<LegalConfigAction> {}
export class DeleteStoreFaqConfigSuccess extends ActionCreator<LegalConfigAction> {}
export class DeleteStoreFaqConfigError extends ActionCreator<LegalConfigAction> {}

export class ResetLegalState extends ActionCreator<LegalConfigAction> {}

export const actions = {
  fetchStoreLegalSetting: () => new FetchStoreLegalSetting(),
  saveStoreLegalSetting: (payload: LegalConfigAction) =>
    new SaveStoreLegalSetting(payload),
  fetchStoreTermsConfig: () => new FetchStoreTermsConfig(),
  saveStoreTermsConfig: (payload: LegalConfigAction) =>
    new SaveStoreTermsConfig(payload),
  fetchStoreShippingsConfig: () => new FetchStoreShippingsConfig(),
  saveStoreShippingsConfig: (payload: LegalConfigAction) =>
    new SaveStoreShippingsConfig(payload),
  fetchStoreRefundsConfig: () => new FetchStoreRefundsConfig(),
  saveStoreRefundsConfig: (payload: LegalConfigAction) =>
    new SaveStoreRefundsConfig(payload),
  saveStoreAboutUsConfig: (payload: LegalConfigAction) =>
    new SaveStoreAboutUsConfig(payload),
  fetchStoreFaqConfig: () => new FetchStoreFaqConfig(),
  saveStoreFaqConfig: (payload: LegalConfigAction) =>
    new SaveStoreFaqConfig(payload),
  saveStoreFaqSetting: (payload: LegalConfigAction) =>
    new SaveStoreFaqSetting(payload),
  deleteStoreFaqConfig: (payload: LegalConfigAction) =>
    new DeleteStoreFaqConfig(payload),
  editStoreFaqConfig: (payload: LegalConfigAction) =>
    new EditStoreFaqConfig(payload),
  fetchStoreAboutUsConfig: () => new FetchStoreAboutUsConfig(),
  resetState: () => new ResetLegalState(),
};

export const initialState = {};

export default createReducer<LegalConfigState>(initialState, {
  [FetchStoreLegalSetting.type]: (state: LegalConfigState) => ({
    ...state,
    isFetchingLegalSetting: true,
    fetchLegalSettingSuccess: undefined,
    fetchLegalSettingError: undefined,
  }),
  [FetchStoreLegalSettingSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingLegalSetting: false,
    legalSetting: action.data,
    fetchLegalSettingSuccess: true,
    fetchLegalSettingError: undefined,
  }),
  [FetchStoreLegalSettingError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingLegalSetting: false,
    fetchLegalSettingSuccess: undefined,
    fetchLegalSettingError: action.error,
  }),
  [SaveStoreLegalSetting.type]: (state: LegalConfigState) => ({
    ...state,
    isSavingLegalSetting: true,
    saveLegalSettingSuccess: undefined,
    saveLegalSettingError: undefined,
  }),
  [SaveStoreLegalSettingSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSavingLegalSetting: false,
    legalSetting: action.data,
    saveLegalSettingSuccess: true,
    saveLegalSettingError: undefined,
  }),
  [SaveStoreLegalSettingError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSavingLegalSetting: false,
    saveLegalSettingSuccess: undefined,
    saveLegalSettingError: action.error,
  }),
  [FetchStoreTermsConfig.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingTerms: true,
    fetchTermsError: undefined,
  }),
  [FetchStoreTermsConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingTerms: undefined,
    terms: action.data,
  }),
  [FetchStoreTermsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingTerms: undefined,
    fetchTermsError: action.error,
  }),
  [SaveStoreTermsConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveTermsError: undefined,
    saveTermsSuccess: undefined,
  }),
  [SaveStoreTermsConfigSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveTermsSuccess: true,
    saveTermsError: undefined,
  }),
  [SaveStoreTermsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveTermsSuccess: undefined,
    saveTermsError: action.error,
  }),
  [FetchStoreFaqConfig.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingFaq: true,
    fetchFaqError: undefined,
  }),
  [FetchStoreFaqConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingFaq: undefined,
    questions: action.data,
  }),
  [FetchStoreFaqConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingFaq: undefined,
    fetchFaqError: action.error,
  }),
  [SaveStoreFaqConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveFaqError: undefined,
    saveFaqSuccess: undefined,
  }),
  [SaveStoreFaqConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => {
    const qs = state.questions
      ? [...state.questions, action.data]
      : [action.data];

    return {
      ...state,
      isSaving: false,
      saveFaqSuccess: true,
      saveFaqError: undefined,
      questions: qs,
    };
  },
  [SaveStoreFaqConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveFaqSuccess: undefined,
    saveFaqError: action.error,
  }),
  [SaveStoreFaqSetting.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveFaqError: undefined,
    saveFaqSuccess: undefined,
  }),
  [SaveStoreFaqSettingSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveFaqError: undefined,
    saveFaqSuccess: true,
  }),
  [SaveStoreFaqSettingError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveFaqError: action.error,
    saveFaqSuccess: undefined,
  }),
  [DeleteStoreFaqConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveFaqError: undefined,
    saveFaqSuccess: undefined,
  }),
  [DeleteStoreFaqConfigSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveFaqError: undefined,
    saveFaqSuccess: true,
  }),
  [DeleteStoreFaqConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveFaqError: action.error,
    saveFaqSuccess: undefined,
  }),
  [EditStoreFaqConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveFaqError: undefined,
    saveFaqSuccess: undefined,
  }),
  [EditStoreFaqConfigSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveFaqError: undefined,
    saveFaqSuccess: true,
  }),
  [EditStoreFaqConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveFaqError: action.error,
    saveFaqSuccess: undefined,
  }),
  [FetchStoreRefundsConfig.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingRefunds: true,
    fetchRefundsError: undefined,
  }),
  [FetchStoreRefundsConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingRefunds: undefined,
    refunds: action.data,
  }),
  [FetchStoreRefundsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingRefunds: undefined,
    fetchRefundsError: action.error,
  }),
  [SaveStoreRefundsConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveRefundsError: undefined,
    saveRefundsSuccess: undefined,
  }),
  [SaveStoreRefundsConfigSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveRefundsSuccess: true,
    saveRefundsError: undefined,
  }),
  [SaveStoreRefundsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveRefundsSuccess: undefined,
    saveRefundsError: action.error,
  }),
  [FetchStoreShippingsConfig.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingShippings: true,
    fetchShippingsError: undefined,
  }),
  [FetchStoreShippingsConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingShippings: undefined,
    fetchShippingsError: undefined,
    shippings: action.data,
  }),
  [FetchStoreShippingsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingShippings: undefined,
    fetchShippingsError: action.error,
  }),
  [SaveStoreShippingsConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveShippingsError: undefined,
    saveShippingsSuccess: undefined,
  }),
  [SaveStoreShippingsConfigSuccess.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: false,
    saveShippingsSuccess: true,
    saveShippingsError: undefined,
  }),
  [SaveStoreShippingsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveShippingsSuccess: undefined,
    saveShippingsError: action.error,
  }),
  [FetchStoreAboutUsConfig.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingAboutUs: true,
    fetchAboutUsError: undefined,
  }),
  [FetchStoreAboutUsConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingAboutUs: undefined,
    fetchAboutUsError: undefined,
    aboutUs: action.data,
  }),
  [FetchStoreAboutUsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isFetchingAboutUs: undefined,
    fetchAboutUsError: action.error,
  }),
  [SaveStoreAboutUsConfig.type]: (state: LegalConfigState) => ({
    ...state,
    isSaving: true,
    saveAboutUsError: undefined,
    saveAboutUsSuccess: undefined,
  }),
  [SaveStoreAboutUsConfigSuccess.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveAboutUsSuccess: true,
    saveAboutUsError: undefined,
    aboutUs: action.data,
  }),
  [SaveStoreAboutUsConfigError.type]: (
    state: LegalConfigState,
    action: LegalConfigAction
  ) => ({
    ...state,
    isSaving: false,
    saveAboutUsSuccess: undefined,
    saveAboutUsError: action.error,
  }),
  [ResetLegalState.type]: (state: LegalConfigState) => ({
    ...state,
    saveShippingsError: undefined,
    saveAboutUsError: undefined,
    saveRefundsError: undefined,
    saveTermsError: undefined,
    saveShippingsSuccess: undefined,
    saveRefundsSuccess: undefined,
    saveTermsSuccess: undefined,
    saveFaqSuccess: undefined,
    SaveStoreFaqSettingSuccess: undefined,
    saveAboutUsSuccess: undefined,
  }),
});

export const fetchStoreLegalSettingEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(
      FetchStoreLegalSetting.type,
      SaveStoreFaqConfigSuccess.type,
      SaveStoreTermsConfigSuccess.type,
      SaveStoreRefundsConfigSuccess.type,
      SaveStoreShippingsConfigSuccess.type,
      SaveStoreAboutUsConfigSuccess.type
    ),
    switchMap(() =>
      getStoreLegalSettingApi().pipe(
        map(data => {
          return new FetchStoreLegalSettingSuccess({data});
        }),
        buildCatchError(FetchStoreLegalSettingError)
      )
    )
  );

export const saveStoreLegalSettingEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreLegalSetting.type),
    switchMap(({legalSetting}) =>
      saveStoreLegalSettingApi(legalSetting).pipe(
        map(data => {
          return new SaveStoreLegalSettingSuccess({data});
        }),
        buildCatchError(SaveStoreLegalSettingError)
      )
    )
  );

export const fetchFaqEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(
      FetchStoreFaqConfig.type,
      DeleteStoreFaqConfigSuccess.type,
      EditStoreFaqConfigSuccess.type
    ),
    switchMap(() =>
      getStoreFaqConfigApi().pipe(
        map(faqPag => {
          return new FetchStoreFaqConfigSuccess({
            data: faqPag.data || [],
          });
        }),
        buildCatchError(FetchStoreFaqConfigError)
      )
    )
  );

export const createFaqEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreFaqConfig.type),
    switchMap(({legalConfigRequest}: LegalConfigAction) => {
      const {enabled, ...rq} = legalConfigRequest;
      return saveStoreFaqConfigApi(rq).pipe(
        map(data => {
          return new SaveStoreFaqConfigSuccess({data});
        }),
        buildCatchError(SaveStoreFaqConfigError)
      );
    })
  );

export const editFaqEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(EditStoreFaqConfig.type),
    switchMap(({legalConfigRequest, id}: LegalConfigAction) => {
      const {enabled, ...rq} = legalConfigRequest;
      return editStoreFaqConfigApi(rq, {
        route: buildRoute(endpoints.EDIT_STORE_FAQ_CONFIG, [id]),
      }).pipe(
        map(data => {
          return new EditStoreFaqConfigSuccess({data});
        }),
        buildCatchError(EditStoreFaqConfigError)
      );
    })
  );

export const deleteFaqEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(DeleteStoreFaqConfig.type),
    switchMap(({legalConfigRequest}: LegalConfigAction) =>
      deleteStoreFaqConfigApi({
        route: buildRoute(endpoints.EDIT_STORE_FAQ_CONFIG, [
          legalConfigRequest.id,
        ]),
      }).pipe(
        map(data => {
          return new DeleteStoreFaqConfigSuccess({data});
        }),
        buildCatchError(DeleteStoreFaqConfigError)
      )
    )
  );

export const fetchTermsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchStoreTermsConfig.type, SaveStoreTermsConfigSuccess.type),
    switchMap(() =>
      getStoreTermsConfigApi().pipe(
        map(data => {
          return new FetchStoreTermsConfigSuccess({
            data,
          });
        }),
        buildCatchError(FetchStoreTermsConfigError)
      )
    )
  );

export const createTermsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreTermsConfig.type),
    switchMap(({legalConfigRequest}: LegalConfigAction) =>
      saveStoreTermsConfigApi(legalConfigRequest).pipe(
        map(data => {
          return new SaveStoreTermsConfigSuccess({data});
        }),
        buildCatchError(SaveStoreTermsConfigError)
      )
    )
  );

export const fetchRefundsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchStoreRefundsConfig.type, SaveStoreRefundsConfigSuccess.type),
    switchMap(() =>
      getStoreRefundsConfigApi().pipe(
        map(data => {
          return new FetchStoreRefundsConfigSuccess({
            data,
          });
        }),
        buildCatchError(FetchStoreRefundsConfigError)
      )
    )
  );

export const createRefundsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreRefundsConfig.type),
    switchMap(({legalConfigRequest}: LegalConfigAction) =>
      saveStoreRefundsConfigApi(legalConfigRequest).pipe(
        map(data => {
          return new SaveStoreRefundsConfigSuccess({data});
        }),
        buildCatchError(SaveStoreRefundsConfigError)
      )
    )
  );

export const fetchShippingsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(
      FetchStoreShippingsConfig.type,
      SaveStoreShippingsConfigSuccess.type
    ),
    switchMap(() =>
      getStoreShippingsConfigApi().pipe(
        map(data => {
          return new FetchStoreShippingsConfigSuccess({
            data,
          });
        }),
        buildCatchError(FetchStoreShippingsConfigError)
      )
    )
  );

export const createShippingsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreShippingsConfig.type),
    switchMap(({legalConfigRequest}: LegalConfigAction) =>
      saveStoreShippingsConfigApi(legalConfigRequest).pipe(
        map(data => {
          return new SaveStoreShippingsConfigSuccess({data});
        }),
        buildCatchError(SaveStoreShippingsConfigError)
      )
    )
  );

export const fetchAboutUsConfigEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchStoreAboutUsConfig.type),
    switchMap(() =>
      getStoreAboutUsConfigApi().pipe(
        map(data => {
          return new FetchStoreAboutUsConfigSuccess({
            data,
          });
        }),
        buildCatchError(FetchStoreAboutUsConfigError)
      )
    )
  );

export const createAboutUsConfigEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreAboutUsConfig.type),
    switchMap(({legalRequest}) => {
      const {arrFilledFile = []} = legalRequest;

      console.log({
        arrFilledFile,
      });

      if (arrFilledFile.length === 0) {
        return of([[], legalRequest, undefined]);
      }

      // Upload images sequentially to guarantee order
      const uploadObservables = arrFilledFile.map(el =>
        uploadImageStoreApi(
          createSingleFieldFormData(legalRequest[el.name], 'file', {
            filePrefix: 'ui/legal',
          })
        ).pipe(
          map((result: StoredFile[] | StoredFile) =>
            Array.isArray(result) ? result[0] : result
          )
        )
      );

      return concat(...uploadObservables).pipe(
        toArray(),
        map((fileImages: StoredFile[]) => [
          fileImages,
          legalRequest,
          undefined,
        ]),
        catchError((error: ApiError) => of([undefined, undefined, error]))
      );
    }),
    concatMap(
      ([
        fileImages: StoredFile[],
        legalRequest: SaveLegalRequest,
        pipeError: ApiError,
      ]) => {
        if (pipeError)
          return of(new SaveStoreAboutUsConfigError({error: pipeError}));
        const {arrFilledFile} = legalRequest;

        if (
          arrFilledFile &&
          fileImages &&
          arrFilledFile.length === fileImages.length
        ) {
          arrFilledFile.forEach((el, idx) => {
            legalRequest[el.id] = fileImages[idx] ? fileImages[idx].id : '';
          });
        }
        delete legalRequest.arrFilledFile;
        const {
          image1,
          image2,
          image3,
          image4,
          image5,
          image6,
          ...rq
        } = legalRequest;

        return saveStoreAboutUsConfigApi(rq).pipe(
          map(data => {
            return new SaveStoreAboutUsConfigSuccess({data});
          }),
          buildCatchError(SaveStoreAboutUsConfigError)
        );
      }
    )
  );
